<template>
  <DialogChecklistModal
    v-if="shouldShowChecklistModal && task.id"
    v-model="openViewTask"
    :task="task"
    @task-updated="handleTaskUpdate"
  />

  <DialogOverlockModal
    v-else-if="shouldShowOverlockModal && task.id"
    v-model="openViewTask"
    :task="task"
    @task-updated="handleTaskUpdate"
  />

  <DialogPreInstallModal
    v-else-if="shouldShowPreInstallModal && task.id"
    v-model="openViewTask"
    :task="task"
    @task-updated="handleTaskUpdate"
  />

  <DialogLayout
    v-else-if="
      !loading &&
      !shouldShowChecklistModal &&
      !shouldShowOverlockModal &&
      !shouldShowPreInstallModal
    "
    v-model="openViewTask"
    :title="task.title"
    width="1000px"
  >
    <AlertsAnimated :alerts="localAlerts" />
    <v-skeleton-loader
      :loading="loading"
      type="list-item-two-line, list-item-two-line, list-item-two-line"
    >
      <dl class="property-list my-4 two-columns">
        <dt>Task ID</dt>
        <div>{{ task.taskId }}</div>
        <dt>Task Name</dt>
        <div>{{ task.title }}</div>
        <dt>Task Status</dt>
        <div>
          <v-select
            v-model="selectedStatus"
            style="max-width: 250px"
            dense
            :items="resolutionStatusItems"
            item-text="text"
            item-value="value"
            @change="() => updateTask('task_status')"
          />
        </div>
        <dt>Task Type</dt>
        <div>{{ titleCase(task.taskType?.name) }}</div>
        <template v-if="hasIncident && incident.type === 'facility_motion'">
          <dt>Units Affected</dt>
          <div>{{ facilityMotionUnitNames || "—" }}</div>
        </template>
        <dt>Due Date</dt>
        <div>
          <v-text-field
            :value="formatDateTime4Humans(task.dueAt)"
            style="max-width: 250px"
            readonly
            solo
            dense
          />
        </div>
        <dt>Performed By</dt>
        <div class="d-flex align-center" style="min-height: 40px">
          <v-icon small left class="mr-1">mdi-account-circle</v-icon>
          <span>{{ loggedInUserName || "—" }}</span>
        </div>
        <dt>Task Priority</dt>
        <div>
          <SeverityChip :severity="titleCase(task.priority)" />
        </div>
        <dt>Task Description</dt>
        <div>
          <v-textarea
            v-model="taskDescription"
            rows="3"
            row-height="15"
            :maxlength="250"
            counter="250"
            :rules="[(v) => v.length <= 250 || 'Max 250 characters']"
            @input="onDescriptionChange"
          />
          <div class="d-flex justify-end mb-5">
            <v-btn
              v-if="isDescriptionChanged"
              class="mr-2"
              small
              @click="cancelDescriptionChange"
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="isDescriptionChanged"
              small
              color="primary"
              @click="() => updateTask('done')"
            >
              Save
            </v-btn>
          </div>
        </div>

        <dt>Add Comment</dt>
        <div>
          <v-textarea
            v-model="newComment"
            placeholder="Add a comment..."
            rows="2"
            dense
            :maxlength="500"
            counter="500"
          />
          <div class="d-flex justify-end mt-2">
            <v-btn
              v-if="newComment.trim()"
              class="mr-2"
              small
              :disabled="isAddingComment"
              @click="cancelComment"
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="newComment.trim()"
              small
              color="primary"
              :loading="isAddingComment"
              :disabled="isAddingComment"
              @click="addComment"
            >
              Add Comment
            </v-btn>
          </div>
        </div>
      </dl>
    </v-skeleton-loader>
    <v-tabs v-model="selectedTab">
      <v-tab>History</v-tab>
      <v-tab>Attachments</v-tab>
      <v-tab>Incident/Task Video</v-tab>
      <v-tab v-if="hasIncidentWithSurveyCapability">Incident Survey</v-tab>
    </v-tabs>

    <v-tabs-items v-model="selectedTab">
      <v-tab-item>
        <CardTaskHistory
          ref="taskHistoryComponent"
          :task-id="task.id"
          @incident-found="setIncidentId"
        />
      </v-tab-item>
      <v-tab-item>
        <CardTaskAttachments
          :task-id="task.id"
          :initial-attachments="task.files"
          @attachment-updated="handleAttachmentUpdate"
        />
      </v-tab-item>
      <v-tab-item>
        <CardTaskEventVideos
          v-if="incidentId"
          :task-id="task.id"
          :incident-id="incidentId"
        />
      </v-tab-item>
      <v-tab-item v-if="hasIncidentWithSurveyCapability">
        <v-tabs v-model="surveySubTab" dense background-color="grey lighten-4">
          <v-tab>Submit Survey</v-tab>
          <v-tab>
            Survey History
            <v-chip
              v-if="!isEmptySurvey && surveyHistoryCount > 0"
              x-small
              color="blue lighten-4"
              text-color="blue darken-3"
              class="ml-1"
            >
              {{ surveyHistoryCount }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="surveySubTab">
          <v-tab-item>
            <div class="pa-4">
              <template v-if="shouldShowSurveyForm">
                <v-alert
                  v-if="isReopenedSurvey"
                  type="warning"
                  outlined
                  class="mb-4"
                >
                  <strong>Resubmission Requested</strong> — This task was
                  reopened. Your previous responses have been pre-filled where
                  available.
                  <div class="caption mt-1">
                    Submitting this survey will
                    <strong>automatically set the Task Status to Done</strong>.
                  </div>
                </v-alert>
                <v-alert v-else type="info" outlined class="mb-4">
                  Submitting this survey will
                  <strong>automatically set the Task Status to Done</strong>.
                </v-alert>
                <FormIncidentSurvey
                  :previous-survey="isReopenedSurvey ? parsedSurveyData : null"
                  @survey-submitted="handleSurveySubmitted"
                />
              </template>
              <v-alert v-else-if="hasCompletedSurvey" type="success" outlined>
                Survey has been submitted for this incident. View the
                <strong>Survey History</strong> tab for details.
              </v-alert>
            </div>
          </v-tab-item>
          <v-tab-item>
            <CardSurveyHistory @count-updated="surveyHistoryCount = $event" />
          </v-tab-item>
        </v-tabs-items>
      </v-tab-item>
    </v-tabs-items>
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import SeverityChip from "@tod-ui/components/SeverityChip.vue";
import {
  UPDATE_TASK,
  GET_TASK,
  TASKS,
  TASK_HISTORY,
  CREATE_TASK_COMMENT,
} from "./graphql";
import { RESOLUTION_STATUS } from "@tod-ui/constants/task_management";
import { titleCase } from "@tod-ui/helpers/strings";
import { formatDate, formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import CardTaskHistory from "./CardTaskHistory.vue";
import { formatName } from "./helper";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import CardTaskAttachments from "./CardTaskAttachments.vue";
import CardTaskEventVideos from "./CardTaskEventVideos.vue";
import FormIncidentSurvey from "@components/incident/FormIncidentSurvey.vue";
import CardSurveyHistory from "@components/incident/CardSurveyHistory.vue";
import { ORGANIZATION_ANCESTORS } from "../../graphql/organizations";
import useIncident from "@components/incident/useIncident";
import DialogChecklistModal from "./DialogChecklistModal.vue";
import DialogOverlockModal from "./DialogOverlockModal.vue";
import DialogPreInstallModal from "./DialogPreInstallModal.vue";

export default {
  name: "DialogViewTask",
  components: {
    DialogLayout,
    SeverityChip,
    CardTaskHistory,
    AlertsAnimated,
    CardTaskAttachments,
    CardTaskEventVideos,
    FormIncidentSurvey,
    CardSurveyHistory,
    DialogChecklistModal,
    DialogOverlockModal,
    DialogPreInstallModal,
  },
  props: {
    taskId: {
      type: String,
      required: true,
    },
    value: {
      type: Boolean,
      required: true,
    },
    incidentIdFromParent: {
      type: String,
      default: null,
    },
    autoOpenSurveyTab: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { defaultOrganization, currentUser } = useUser();
    const { addLocalAlert, clearAllLocalAlerts, localAlerts } = useAlerts();
    const { incident, fetchIncident, facilityMotionUnitNames } = useIncident();

    return {
      defaultOrganization,
      currentUser,
      addLocalAlert,
      clearAllLocalAlerts,
      localAlerts,
      incident,
      fetchIncident,
      facilityMotionUnitNames,
    };
  },
  data() {
    return {
      resolutionStatusItems: [
        { text: "To Do", value: RESOLUTION_STATUS.TODO },
        { text: "In Progress", value: RESOLUTION_STATUS.IN_PROGRESS },
        { text: "In Review", value: RESOLUTION_STATUS.IN_REVIEW },
        { text: "Done", value: RESOLUTION_STATUS.DONE },
        { text: "Cancelled", value: RESOLUTION_STATUS.CANCELLED },
        { text: "Pending", value: RESOLUTION_STATUS.PENDING },
        { text: "Reopened", value: RESOLUTION_STATUS.REOPENED },
      ],
      selectedStatus: RESOLUTION_STATUS.TODO,
      selectedAssignee: null,
      taskDescription: "",
      originalDescription: "",
      isDescriptionChanged: false,
      selectedTab: 0,
      surveySubTab: 0,
      task: {},
      assigneeOptions: [],
      comments: [],
      parentFacilityId: "",
      incidentId: null,
      newComment: "",
      isAddingComment: false,
      surveyHistoryCount: 0,
      surveyJustSubmitted: false,
    };
  },
  computed: {
    loading() {
      return this.$apollo.queries.task.loading;
    },
    openViewTask: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    refetchTasks() {
      return [
        {
          query: TASKS,
          variables: {
            parentId: this.defaultOrganization.id,
            resolutions: [RESOLUTION_STATUS.TODO],
          },
        },
        {
          query: TASK_HISTORY,
          variables: {
            taskId: this.taskId,
          },
        },
      ];
    },
    hasIncident() {
      return !!(this.incidentId && this.incident);
    },
    hasIncidentWithSurveyCapability() {
      if (!this.hasIncident) return false;
      if (this.incident.type === "facility_motion") return true;
      // Show the survey tab only when:
      // 1. Incident needs a survey — same logic as needsSurvey:
      //    (closed/muted + denied) or (closed/muted + hasDisabledLienResponder) or
      //    (queuedUntilDawn + unexplained)
      // 2. Incident already has a completed survey — to view history
      // 3. Incident belongs to a zone (camera) and is no longer active — zone incidents
      //    use a different resolution path so we check status directly instead of resolution
      const { status, organization, needsSurvey, surveyed, resolution } =
        this.incident;
      return (
        needsSurvey ||
        surveyed ||
        (["closed", "muted"].includes(status) &&
          resolution === "unexplained") ||
        (organization?.subtype === "zone" &&
          ["muted", "closed", "resolved"].includes(status))
      );
    },
    shouldShowSurveyForm() {
      if (!this.hasIncidentWithSurveyCapability) return false;
      if (this.surveyJustSubmitted) return false;
      if (this.isEmptySurvey) return true;
      return this.selectedStatus === RESOLUTION_STATUS.REOPENED;
    },
    hasCompletedSurvey() {
      return (
        this.hasIncidentWithSurveyCapability &&
        (this.surveyJustSubmitted || !this.isEmptySurvey) &&
        this.selectedStatus === RESOLUTION_STATUS.DONE
      );
    },
    isReopenedSurvey() {
      return this.selectedStatus === RESOLUTION_STATUS.REOPENED;
    },
    isEmptySurvey() {
      if (!this.incident?.survey) return true;

      // Check if survey is an empty JSON string
      if (typeof this.incident.survey === "string") {
        const trimmedSurvey = this.incident.survey.trim();
        return trimmedSurvey === "{}" || trimmedSurvey === "";
      }

      // Check if survey is an empty object
      if (typeof this.incident.survey === "object") {
        // Handle case where survey is an empty object
        if (Object.keys(this.incident.survey).length === 0) {
          return true;
        }

        // Check for invalid insertedAt which has invalid date
        if (
          this.incident.survey.insertedAt &&
          typeof this.incident.survey.insertedAt === "string" &&
          this.incident.survey.insertedAt.includes("Invalid DateTime")
        ) {
          return true;
        }

        // Check if all values are empty or invalid
        const validFields = Object.entries(this.incident.survey).filter(
          ([key, value]) => {
            // Skip insertedAt field for this check
            if (key === "insertedAt") return false;

            return (
              value &&
              value !== "" &&
              value !== "{}" &&
              !(typeof value === "string" && value.includes("Invalid"))
            );
          }
        );

        return validFields.length === 0;
      }

      return true;
    },
    parsedSurveyData() {
      if (this.incident?.survey && !this.isEmptySurvey) {
        // If survey is already an object, return it
        if (typeof this.incident.survey === "object") {
          return this.incident.survey;
        }
        // If survey is a JSON string, parse it
        try {
          const parsed = JSON.parse(this.incident.survey);
          // Double check it's not empty after parsing
          return Object.keys(parsed).length > 0 ? parsed : null;
        } catch (error) {
          console.error("Error parsing survey data:", error);
          return null;
        }
      }
      return null;
    },
    shouldShowChecklistModal() {
      return (
        this.hasChecklist(this.task) &&
        !this.loading &&
        !this.isManageOverlockTask(this.task) &&
        !this.isPreInstallTask(this.task)
      );
    },
    shouldShowOverlockModal() {
      return this.isManageOverlockTask(this.task) && !this.loading;
    },
    shouldShowPreInstallModal() {
      return this.isPreInstallTask(this.task) && !this.loading;
    },
    // Display the logged-in user's name in place of the editable Assignee
    // selector (assignee picking is no longer surfaced in this dialog).
    loggedInUserName() {
      const u = this.currentUser;
      if (!u) return "";
      const first = u.firstName || "";
      const last = u.lastName || "";
      const full = `${first} ${last}`.trim();
      return full || u.username || "";
    },
  },
  apollo: {
    task: {
      query: GET_TASK,
      variables() {
        return {
          id: this.taskId,
        };
      },
      update(data) {
        if (data.findTask) {
          const { resolution, assignee, description } = data.findTask;
          this.selectedStatus = resolution;
          this.selectedAssignee = assignee?.id;
          this.taskDescription = description;
          this.originalDescription = description;

          if (assignee) {
            this.assigneeOptions = [
              {
                id: assignee.id,
                name: formatName(assignee),
              },
            ];
          }
          if (
            data.findTask.organization?.type === "facility" ||
            data.findTask.taskType?.name === "custom" ||
            data.findTask.taskType?.name === "maintenance"
          ) {
            this.parentFacilityId = data.findTask.organization.id;
          }
        }
        return data.findTask;
      },
    },
    organizationAncestors: {
      query: ORGANIZATION_ANCESTORS,
      variables() {
        return {
          id: this.task?.organization?.id,
          types: ["facility"],
        };
      },
      update(data) {
        this.parentFacilityId = data.organization.ancestors[0].id;
      },
      skip() {
        return !this.task?.organization?.id;
      },
    },
  },
  watch: {
    incidentId: {
      handler(newIncidentId) {
        if (newIncidentId) {
          this.fetchIncident(this.$apollo, newIncidentId);
        }
      },
      immediate: true,
    },
    autoOpenSurveyTab: {
      handler(shouldOpen) {
        if (shouldOpen && this.hasIncidentWithSurveyCapability) {
          this.selectedTab = 3;
        }
      },
      immediate: true,
    },
    hasIncidentWithSurveyCapability: {
      handler(hasCapability) {
        if (hasCapability && this.autoOpenSurveyTab) {
          this.selectedTab = 3;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.incidentIdFromParent) {
      this.incidentId = this.incidentIdFromParent;
    }
  },
  methods: {
    setIncidentId(id) {
      this.incidentId = id;
    },
    updateAssignee(newAssigneeId) {
      if (this.selectedAssignee === newAssigneeId) {
        return;
      }

      this.selectedAssignee = newAssigneeId;
      this.updateTask("assignee");
    },
    async updateTask(_operation) {
      this.clearAllLocalAlerts();
      try {
        let response = await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.taskId,
            assigneeId: this.selectedAssignee,
            description: this.taskDescription,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: this.selectedStatus,
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
          },
          refetchQueries: this.refetchTasks,
        });
        if (response.data.updateTask) {
          this.addLocalAlert({
            type: "success",
            message: "Task updated successfully",
            timeout: 15,
          });
          this.handleTaskUpdate(response.data.updateTask);
        }
      } catch (error) {
        this.addLocalAlert({
          type: "error",
          message: error.message || "Failed to update task",
          timeout: 15,
        });
      }
    },
    async addComment() {
      if (!this.newComment.trim()) return;

      this.isAddingComment = true;
      try {
        await this.$apollo.mutate({
          mutation: CREATE_TASK_COMMENT,
          variables: {
            taskId: this.taskId,
            content: this.newComment.trim(),
          },
          refetchQueries: [
            {
              query: TASK_HISTORY,
              variables: {
                taskId: this.taskId,
              },
            },
          ],
        });

        if (this.$refs.taskHistoryComponent) {
          await this.$refs.taskHistoryComponent.$apollo.queries.taskHistory.refetch();
        }

        this.addLocalAlert({
          type: "success",
          message: "Comment added successfully",
          timeout: 15,
        });

        this.newComment = "";
      } catch (error) {
        console.error("Error adding comment:", error);
        this.addLocalAlert({
          type: "error",
          message: "Failed to add comment",
          timeout: 15,
        });
      } finally {
        this.isAddingComment = false;
      }
    },
    cancelComment() {
      this.newComment = "";
    },
    async handleSurveySubmitted() {
      this.surveyJustSubmitted = true;
      try {
        this.addLocalAlert({
          type: "success",
          message: "Survey submitted successfully",
          timeout: 15,
        });

        this.selectedStatus = RESOLUTION_STATUS.DONE;
        await this.updateTask("survey_submitted");
      } catch (error) {
        console.error("Error handling survey submission:", error);
        this.addLocalAlert({
          type: "error",
          message: "Survey submitted but failed to update task status",
          timeout: 15,
        });
      }
    },
    async handleAttachmentUpdate() {
      if (this.$refs.taskHistoryComponent) {
        await this.$refs.taskHistoryComponent.$apollo.queries.taskHistory.refetch();
      }
    },
    titleCase,
    formatDateTime4Humans(date) {
      if (this.task.taskType?.name === "incident_resolution") {
        return formatDate(date, this.defaultOrganization.timeZone);
      }
      return formatDateSlashUTC(date);
    },
    onDescriptionChange() {
      this.isDescriptionChanged =
        this.taskDescription !== this.originalDescription;
    },
    cancelDescriptionChange() {
      this.taskDescription = this.originalDescription;
      this.isDescriptionChanged = false;
    },
    hasChecklist(task) {
      return (
        task?.actions?.checklist &&
        Array.isArray(task.actions.checklist) &&
        task.actions.checklist.length > 0
      );
    },
    isManageOverlockTask(task) {
      return task?.taskType?.name?.toLowerCase() === "manage_overlocks";
    },
    isPreInstallTask(task) {
      return task?.taskType?.name?.toLowerCase() === "pre_install_devices";
    },
    handleTaskUpdate(updateData) {
      this.$emit("task-updated", updateData);
    },
  },
};
</script>

<style lang="scss" scoped>
.property-list {
  dd {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &.two-columns {
    grid-template-columns: repeat(2, max-content auto);
  }
}
</style>
